import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import { Download, Close, Send } from "@mui/icons-material";
import { RightPanel } from "../../../components/RightPanel";

import { useDispatchWrapper } from "../../../hooks";
import {
  ILeadFoodMenuPreviewData,
  ILeadFoodMenuPreviewProps,
} from "./LeadFoodMenu.types";
import {
  generateAndDownloadPdf,
  generatePdfFile,
} from "../../../ejs/Helper/helpers";
import { PDFViewer } from "@react-pdf/renderer";
import { LeadFoodMenuEjs } from "../../../ejs/LeadFoodMenuEjs";
import { previewAndDownloadLeadFoodMenuAsync } from "../../../redux/food/foodActions";
import { IWhatsAppActivityPayload } from "../../../redux/activity/activity.types";
import { showMessage } from "../../../redux/messages/messagesActions";
import { upsertWhatsAppMessage } from "../../../redux/activity/activityActions";
import { uploadFile } from "../../../components/FileUpload/utils";
import { useBillingCompanyContext } from "../../../providers";
import { useAuth } from "../../../contexts/AuthProvider";

export const LeadFoodMenuPreview: React.FC<ILeadFoodMenuPreviewProps> = (
  props,
) => {
  const { open, uuid, onClose, selectedLeadData, frontendPreviewData } = props;

  const {whatsappService} = useAuth();
  const auth = useAuth();
  const dispatch = useDispatchWrapper();
  const company = useBillingCompanyContext();

  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] =
    React.useState<ILeadFoodMenuPreviewData | null>(null);

  const fetchPreviewData = React.useCallback(
    (preview_only = true) => {
      setLoading(true);
      dispatch(
        previewAndDownloadLeadFoodMenuAsync(
          uuid,
          preview_only,
          (isSuccess: boolean, data?: ILeadFoodMenuPreviewData) => {
            if (isSuccess && data) {
              setPreviewData(data);
            }

            setLoading(false);
          },
        ),
      );
    },
    [dispatch, uuid],
  );

  useEffect(() => {
    if (open) {
      if (frontendPreviewData) {
        // Use frontend-built data directly — no API call needed
        const builtData: ILeadFoodMenuPreviewData = {
          company: {
            name: company.comapny.company_outlet_name || auth.app_info.company_name || "",
            phone: null,
            logo: auth.app_info.company_logo || "",
          },
          lead: {
            guest_name: selectedLeadData.name_of_guest || "",
            event_type: selectedLeadData.event_name || "",
            event_time: "",
            guests: selectedLeadData.number_of_guests || 0,
            event_date: selectedLeadData.function_date || "",
          },
          menu: frontendPreviewData.menu,
        };
        setPreviewData(builtData);
      } else if (uuid) {
        fetchPreviewData();
      }
    }
  }, [open, uuid, frontendPreviewData]);

  const handleDownload = async () => {
    if (previewData) {
      try {
        await generateAndDownloadPdf(previewData);
      } catch (err) {
        console.error("Error generating PDF:", err);
        setError("Failed to generate PDF. Please try again.");
      }
    }
  };

  const handleWhatsAppSend = async () => {
    setIsSending(true);

    try {
      let headerUrl = "";

      //sample previewData for testing
      // let previewData = {
      //   company: {
      //     name: "Edgenroots Solutions Pvt Ltd",
      //     phone: null,
      //     logo: `${config.assets.s3BaseUrl}/`,
      //   },
      //   lead: {
      //     guest_name: "Test Monika",
      //     event_type: "Anniversary",
      //     event_time: "Lunch",
      //     guests: 100,
      //     event_date: "2026-01-30T18:30:00.000Z",
      //   },
      //   menu: {
      //     title: "Test Lead Menu  12",
      //     description: "",
      //     categories: [
      //       {
      //         food_items: [
      //           {
      //             food_type: null,
      //             item_name: "Masala Lemon Mojito",
      //             category_list: [
      //               {
      //                 status: "ACTIVE",
      //                 food_items: [],
      //                 category_name: "House Mixology",
      //                 category_uuid: "0b416601-5599-4516-8ded-6894cfc50bfd",
      //                 subcategories: [],
      //                 company_outlet_uuid:
      //                   "a79b44a6-0e2a-4840-b19e-4e2168aa9e12",
      //                 category_description: "",
      //               },
      //             ],
      //             food_item_uuid: "37e05656-efb5-4f22-8615-0e074679bc9e",
      //             item_description: "",
      //           },
      //           {
      //             food_type: null,
      //             item_name: "Classic Mint Mojito",
      //             category_list: [
      //               {
      //                 status: "ACTIVE",
      //                 food_items: [],
      //                 category_name: "House Mixology",
      //                 category_uuid: "0b416601-5599-4516-8ded-6894cfc50bfd",
      //                 subcategories: [],
      //                 company_outlet_uuid:
      //                   "a79b44a6-0e2a-4840-b19e-4e2168aa9e12",
      //                 category_description: "",
      //               },
      //             ],
      //             food_item_uuid: "17801ab9-6295-4a8e-b00c-75b843ec5b84",
      //             item_description: "",
      //           },
      //         ],
      //         category_name: "House Mixology",
      //         category_uuid: "0b416601-5599-4516-8ded-6894cfc50bfd",
      //         max_selection: 2,
      //         subcategories: [
      //           {
      //             food_items: [],
      //             max_selection: 15,
      //             subcategory_name: "Soda Based Non-Alcoholic Beverages",
      //             food_subcategory_name_uuid:
      //               "3acf4894-59d2-4be5-b23a-514ae27627c5",
      //           },
      //         ],
      //       },
      //       {
      //         food_items: [
      //           {
      //             food_type: null,
      //             item_name: "Palak Shorba",
      //             category_list: [
      //               {
      //                 status: "ACTIVE",
      //                 food_items: [],
      //                 category_name: "Soup Selection",
      //                 category_uuid: "bf486c4b-db66-4dfe-ac0a-a0ae7b52a9d8",
      //                 subcategories: [],
      //                 company_outlet_uuid:
      //                   "a79b44a6-0e2a-4840-b19e-4e2168aa9e12",
      //                 category_description: "",
      //               },
      //             ],
      //             food_item_uuid: "349f5152-3974-4e34-a0a7-2224c934fcc3",
      //             item_description: "",
      //           },
      //           {
      //             food_type: null,
      //             item_name: "Tamatar Dhaniya Ka Shorba",
      //             category_list: [
      //               {
      //                 status: "ACTIVE",
      //                 food_items: [],
      //                 category_name: "Soup Selection",
      //                 category_uuid: "bf486c4b-db66-4dfe-ac0a-a0ae7b52a9d8",
      //                 subcategories: [],
      //                 company_outlet_uuid:
      //                   "a79b44a6-0e2a-4840-b19e-4e2168aa9e12",
      //                 category_description: "",
      //               },
      //             ],
      //             food_item_uuid: "0bcae7e0-8322-429c-a155-c989cdf3d66c",
      //             item_description: "",
      //           },
      //           {
      //             food_type: null,
      //             item_name: "French Onion Soup",
      //             category_list: [
      //               {
      //                 status: "ACTIVE",
      //                 food_items: [],
      //                 category_name: "Soup Selection",
      //                 category_uuid: "bf486c4b-db66-4dfe-ac0a-a0ae7b52a9d8",
      //                 subcategories: [],
      //                 company_outlet_uuid:
      //                   "a79b44a6-0e2a-4840-b19e-4e2168aa9e12",
      //                 category_description: "",
      //               },
      //             ],
      //             food_item_uuid: "9c2d0fd5-cbeb-4adb-b4bf-e5f24e7bcb0a",
      //             item_description: "",
      //           },
      //         ],
      //         category_name: "Soup Selection",
      //         category_uuid: "bf486c4b-db66-4dfe-ac0a-a0ae7b52a9d8",
      //         max_selection: 3,
      //         subcategories: [
      //           {
      //             food_items: [],
      //             max_selection: 3,
      //             subcategory_name: "Indian Concoction",
      //             food_subcategory_name_uuid:
      //               "3f14008d-5bdb-4fee-9093-09ff1dc4ecdf",
      //           },
      //           {
      //             food_items: [],
      //             max_selection: 2,
      //             subcategory_name: "Continental Accent",
      //             food_subcategory_name_uuid:
      //               "a02a972e-32c2-4068-a03f-ba93218be7b8",
      //           },
      //         ],
      //       },
      //     ],
      //   },
      // };

      const generatedPdf = await generatePdfFile(previewData);

      headerUrl = await uploadFile({
        file: generatedPdf,
        module_name: "WHATSAPP_MARKET_CAMPAIGN",
        defaultReturn: generatedPdf.name || "",
        asPayload: {
          company_outlet_uuid: company.comapny.company_outlet_uuid,
        },
        onWarning: (message) => {
          dispatch(
            showMessage({
              type: "warning",
              message: message,
              displayAs: "snackbar",
            }),
          );
        },
      });
      const payload: IWhatsAppActivityPayload = {
        fromNumber: null,
        toNumber: selectedLeadData.contact_number_one,
        is_automatic_message: 0,
        company_outlet_uuid: company.comapny.company_outlet_uuid,
        company_outlet_name: company.comapny.company_outlet_name,
        whatsapp_web_service: false,
        document: { link: headerUrl, caption: "" },
      };

      dispatch(
        upsertWhatsAppMessage(payload, whatsappService.selectedService, (isSuccess) => {
          setIsSending(false);
          dispatch(
            showMessage({
              type: isSuccess ? "success" : "error",
              message: isSuccess
                ? "Test message sent successfully"
                : "Failed to send test message",
              displayAs: "snackbar",
            }),
          );
        }),
      );
    } catch (error) {
      setIsSending(false);
      dispatch(
        showMessage({
          type: "error",
          message: "File upload failed",
          displayAs: "snackbar",
        }),
      );
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={2}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="outlined"
            onClick={() => fetchPreviewData()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      );
    }

    if (!previewData) {
      return (
        <Box p={2}>
          <Typography>No data available</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "& .react-pdf__toolbar": {
            display: "none !important",
          },
          "& .react-pdf__message": {
            display: "none !important",
          },
          "& .react-pdf__Document": {
            height: "100% !important",
          },
          "& .react-pdf__Page": {
            height: "100% !important",
          },
        }}
      >
        <PDFViewer
          style={{ width: "100%", height: "100%", minHeight: "600px" }}
        >
          <LeadFoodMenuEjs data={previewData} />
        </PDFViewer>
      </Box>
    );
  };

  return (
    <RightPanel
      open={open}
      heading={`Lead Food Menu Preview`}
      width="50%"
      onClose={onClose}
      actionButtons={() => (
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button variant="outlined" startIcon={<Close />} onClick={onClose}>
            Close
          </Button>
          <Box sx={{ gap: 2, display: "flex" }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownload}
              disabled={!previewData || loading}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleWhatsAppSend}
              disabled={!previewData || loading}
            >
              Send PDF
            </Button>
          </Box>
        </Stack>
      )}
    >
      {renderContent()}
    </RightPanel>
  );
};
